import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, Instagram, Leaf } from 'lucide-react';
import { BRAND, NAV_LINKS } from '@/lib/constants';

export default function Footer() {
  return (
    <footer data-testid="site-footer" className="relative block-ink">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-10 lg:px-16 py-16 sm:py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="flex items-start gap-4">
              <div className="bg-[hsl(var(--c-cream))] p-2 border-2 border-[hsl(var(--c-cream))]">
                <img
                  src={BRAND.logos.mark}
                  alt="Florida SunKissed Mangoes mark"
                  width="72"
                  height="72"
                  decoding="async"
                  loading="lazy"
                  className="h-16 w-16 object-contain"
                />
              </div>
              <div>
                <div className="font-heading text-2xl font-bold leading-tight text-[hsl(var(--c-cream))]">
                  Florida SunKissed Mangoes
                </div>
                <div className="text-xs sm:text-sm text-[hsl(var(--c-cream))]/70 mt-1 uppercase tracking-wider font-semibold">{BRAND.parentLine}</div>
              </div>
            </div>
            <p className="mt-6 text-sm text-[hsl(var(--c-cream))]/80 leading-relaxed max-w-md">
              {BRAND.footerTagline} Florida-grown mangoes sourced directly from local growers and delivered to consumers, retailers, restaurants, distributors, and event vendors.
            </p>
            <div className="mt-6 flex flex-col gap-3 text-sm">
              <a href={`mailto:${BRAND.contact.email}`} className="inline-flex items-center gap-3 text-[hsl(var(--c-cream))]/90 hover:text-[hsl(var(--c-mango-yellow))] btn-transition w-fit" data-testid="footer-email-link">
                <span className="h-9 w-9 border-2 border-[hsl(var(--c-cream))]/30 flex items-center justify-center"><Mail className="h-4 w-4" /></span>
                {BRAND.contact.email}
              </a>
              <a href={BRAND.contact.phoneHref} className="inline-flex items-center gap-3 text-[hsl(var(--c-cream))]/90 hover:text-[hsl(var(--c-mango-yellow))] btn-transition w-fit" data-testid="footer-phone-link">
                <span className="h-9 w-9 border-2 border-[hsl(var(--c-cream))]/30 flex items-center justify-center"><Phone className="h-4 w-4" /></span>
                {BRAND.contact.phone}
              </a>
              <a
                href="https://www.instagram.com/floridamangoes/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 text-[hsl(var(--c-cream))]/90 hover:text-[hsl(var(--c-mango-yellow))] btn-transition w-fit"
                data-testid="footer-instagram-link"
              >
                <span className="h-9 w-9 border border-[hsl(var(--c-cream))]/40 flex items-center justify-center"><Instagram className="h-4 w-4" /></span>
                @floridamangoes
              </a>
            </div>
          </div>

          <div className="md:col-span-3">
            <h4 className="eyebrow text-[hsl(var(--c-mango-yellow))]">Explore</h4>
            <ul className="mt-5 space-y-3 text-sm">
              {NAV_LINKS.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="text-[hsl(var(--c-cream))]/85 hover:text-[hsl(var(--c-mango-yellow))] btn-transition font-semibold">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-4">
            <h4 className="eyebrow text-[hsl(var(--c-mango-yellow))]">Florida-grown. Locally sourced.</h4>
            <div className="mt-5 flex items-start gap-3 text-sm text-[hsl(var(--c-cream))]/85">
              <Leaf className="h-5 w-5 mt-0.5 text-[hsl(var(--c-mango-yellow))] shrink-0" />
              <p>
                We work with homeowners, property managers, and farms across Palm Beach, Broward, and Miami-Dade to bring premium tree-ripened mangoes from South Florida groves to markets across America.
              </p>
            </div>
          </div>
        </div>

        <div className="editorial-rule bg-[hsl(var(--c-cream))]/20 mt-14" />
        <div className="mt-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs text-[hsl(var(--c-cream))]/60">
          <div className="uppercase tracking-wider font-semibold">© {new Date().getFullYear()} Florida SunKissed Mangoes — {BRAND.parentLine}</div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
            <span className="font-mono">{BRAND.tagline}</span>
            <a
              href="https://app.emergent.sh/?utm_source=emergent-badge"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-emergent-link"
              className="inline-flex items-center gap-1.5 text-[hsl(var(--c-cream))]/55 hover:text-[hsl(var(--c-mango-yellow))] btn-transition"
              title="Made with Emergent — visit app.emergent.sh"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                <path d="M15.5702 8.13142C15.7729 8.0412 16.0007 8.18878 15.9892 8.4103C15.8374 11.3192 14.0965 14.0405 11.2531 15.3065C8.40964 16.5725 5.2224 16.0453 2.95912 14.2117C2.78676 14.072 2.82955 13.804 3.03219 13.7137L4.95677 12.8568C5.04866 12.8159 5.15446 12.823 5.24204 12.8725C6.73377 13.7153 8.59176 13.8649 10.2772 13.1145C11.9626 12.3641 13.0947 10.8833 13.4665 9.21075C13.4883 9.11256 13.5539 9.02918 13.6457 8.98827L15.5702 8.13142Z" />
                <path fillRule="evenodd" clipRule="evenodd" d="M15.3066 4.74698L15.5067 5.19653C15.5759 5.35178 15.5061 5.53366 15.3508 5.60278L1.29992 11.8586C1.14467 11.9278 0.962794 11.8579 0.893675 11.7027L0.701732 11.2716L0.693457 11.2531C-1.10317 7.21778 0.711626 2.49007 4.74692 0.693443C8.78221 -1.10318 13.51 0.711693 15.3066 4.74698ZM2.82356 8.55367C2.63552 8.63739 2.41991 8.51617 2.40853 8.31065C2.28373 6.05724 3.53858 3.85787 5.72286 2.88536C7.90715 1.91286 10.3813 2.45199 11.9724 4.05256C12.1175 4.19854 12.0633 4.43988 11.8753 4.5236L2.82356 8.55367Z" />
              </svg>
              <span>Made with Emergent</span>
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
